import { useHealthCardExpiryCheck } from '@/modules/Attendance/hooks/useHealthCardExpiryCheck'
import scheduledServices from '@/modules/Attendance/submodules/Scheduled/scheduled-services'
import { IHealthInsuranceCard } from '@/modules/Attendance/submodules/Scheduled/scheduled-types'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { useCallback, useMemo, useState } from 'react'
import { useEffectOnce } from '../useEffectOnce'
import { toast } from '@conexasaude/conexa-hero'
import { getFirstName } from '@/utils/string'
import { getErrorMessage } from '@/utils/fnUtils'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import { immediateEventBySpeciality } from '@/modules/MedicalSpecialities/utils'
import { SPECIALTY } from './types'
import { PreCallTestLocationState } from '@/modules/MedicalSpecialities/pages/types'
import { useNavigation } from '../useNavigation'
import { useMedicalSpecialitiesContext } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import { useSettingUpAnalyticsEvent } from '../useSettingUpAnalytics'
import { useScreeningData } from '@/modules/Screening/contexts/screeningContext'
import { useAttendanceContext } from '@/modules/Attendance/contexts/AttendanceContex'
import Attendance from '@/modules/Attendance/classes/Attendance'
import { useGetClinicOfficeHours } from '@/modules/MedicalSpecialities/queries'
import { NAVBAR_MODALS } from '@/components/Navbar/types'

export const useSOSMentalHealth = () => {
  const { logAnalyticsEvent } = useFirebaseContext()
  const { closeModal, showModal } = useModalContext()
  const navigate = useNavigation()

  const { setScreeningData } = useScreeningData()
  const { setAttendance } = useAttendanceContext()

  const { specialities } = useMedicalSpecialitiesContext()
  const psychologistSpecialty = specialities?.find(
    item => item.id === SPECIALTY.PSICOLOGO,
  )

  const settingUpAnalyticsEvent = useSettingUpAnalyticsEvent()

  const { user, clinic, userHasIncompleteData } = useAuthContext()
  const { checkPatientEligibility, showUpdateHealthCardModal } =
    useHealthCardExpiryCheck()

  const [immediateAttendanceLoading, setImmediateAttendanceLoading] =
    useState<boolean>(false)

  const [insuranceCards, setInsuranceCards] = useState<IHealthInsuranceCard[]>()

  const { mutateAsync: getClinicOfficeHours } = useGetClinicOfficeHours()

  const goToPreCallTestPage = useCallback(() => {
    let updateDataRoute = '/atualizar-dados'

    if (!userHasIncompleteData) {
      updateDataRoute += '?skip=true'
    }
    const locationState: PreCallTestLocationState = {
      from: '/',
      to: updateDataRoute,
      forwardState: {
        speciality: SPECIALTY.PSICOLOGO,
      },
    }

    navigate('/teste-de-chamada', locationState)
  }, [userHasIncompleteData, navigate])

  const handleHealthCardCheckOrRedirect = useCallback(async () => {
    try {
      settingUpAnalyticsEvent({
        eventName: 'at_sos_mental_health_modal',
        interaction: 'request',
      })
      await getClinicOfficeHours(clinic?.id)
      const isEligible = await checkPatientEligibility()
      if (!isEligible) {
        showUpdateHealthCardModal()
      } else {
        closeModal()
        goToPreCallTestPage()
        settingUpAnalyticsEvent({
          eventName: 'at_sos_mental_health_modal',
          interaction: 'request_success',
        })
      }
    } catch {
      showModal(NAVBAR_MODALS.SOS_ERROR_MENTAL_HEALTH)
      settingUpAnalyticsEvent({
        eventName: 'at_sos_mental_health_modal',
        interaction: 'request_error',
      })
    }
  }, [
    checkPatientEligibility,
    clinic?.id,
    closeModal,
    getClinicOfficeHours,
    goToPreCallTestPage,
    settingUpAnalyticsEvent,
    showModal,
    showUpdateHealthCardModal,
  ])

  useEffectOnce(() => {
    if (clinic?.hasMultipleHealthInsuranceCards) {
      scheduledServices
        .getHealthInsuranceCards()
        .then(res => {
          const cards = res
          setInsuranceCards(cards)
        })
        .catch(err => {
          const errorMessage =
            err?.response?.data?.object?.msg ||
            err?.response?.data?.msg ||
            err?.message

          toast({
            message: errorMessage,
            variant: 'error',
          })
        })
    }
  })

  const checkInsuranceAndRedirect = useCallback(async () => {
    try {
      if (immediateEventBySpeciality.PSICOLOGO) {
        logAnalyticsEvent(immediateEventBySpeciality.PSICOLOGO)
      }

      if (clinic?.hasMultipleHealthInsuranceCards && !insuranceCards?.length) {
        showModal('NO_HEALTH_INSURANCE_MODAL', {
          title: 'Carteirinha do convênio',
        })
      } else {
        await handleHealthCardCheckOrRedirect()
      }
    } catch (error) {
      toast({
        message: getErrorMessage(error),
        variant: 'error',
      })
    }
  }, [
    clinic?.hasMultipleHealthInsuranceCards,
    handleHealthCardCheckOrRedirect,
    insuranceCards?.length,
    logAnalyticsEvent,
    showModal,
  ])

  const handleRequestImmediateAttendance = useCallback(async () => {
    try {
      setScreeningData(screening => {
        screening.professionalType = SPECIALTY.PSICOLOGO
        return screening
      })

      setAttendance(() => {
        const att = new Attendance(psychologistSpecialty)
        return att
      })

      settingUpAnalyticsEvent({
        eventName: 'at_sos_mental_health_modal',
        interaction: 'request',
      })
      setImmediateAttendanceLoading(true)

      const hasChildrenDependents = user?.childrenDependents?.length > 0

      if (hasChildrenDependents) {
        showModal(NAVBAR_MODALS.SOS_CONFIRM_PATIENT, {
          title: `${getFirstName(user?.name)}, esta consulta será para você?`,
          width: 480,
        })
      } else {
        await checkInsuranceAndRedirect()
      }

      settingUpAnalyticsEvent({
        eventName: 'at_sos_mental_health_modal',
        interaction: 'request_success',
      })
    } catch (error) {
      settingUpAnalyticsEvent({
        eventName: 'at_sos_mental_health_modal',
        interaction: 'request_error',
      })
      toast({
        message: getErrorMessage(error),
        variant: 'error',
      })
    } finally {
      setImmediateAttendanceLoading(false)
    }
  }, [
    checkInsuranceAndRedirect,
    psychologistSpecialty,
    setAttendance,
    setScreeningData,
    settingUpAnalyticsEvent,
    showModal,
    user?.childrenDependents,
    user?.name,
  ])

  const enableSOSMentalHealth = useMemo(() => {
    return (
      psychologistSpecialty &&
      psychologistSpecialty.isEnabled() &&
      psychologistSpecialty?.canImmediately
    )
  }, [psychologistSpecialty])

  return {
    immediateAttendanceLoading,
    handleHealthCardCheckOrRedirect,
    handleRequestImmediateAttendance,
    goToPreCallTestPage,
    psychologistSpecialty,
    enableSOSMentalHealth,
    checkInsuranceAndRedirect,
  }
}
